import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Divider, Stack, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Snackbar, Alert, InputAdornment, Avatar, MenuItem, FormControl, InputLabel, Select, Checkbox, useMediaQuery
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ImageIcon from '@mui/icons-material/Image';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SearchIcon from '@mui/icons-material/Search';
import { getFilteredCustomers, sendCustomerCampaign, getApiError } from '../../../utils/api';
import { useTheme } from '@mui/material/styles';

const SEGMENTS = [
  { label: 'All', value: 'all' },
  { label: 'High Value', value: 'high' },
  { label: 'Loyal', value: 'loyal' },
  { label: 'At Risk', value: 'risk' },
  { label: 'New', value: 'new' },
];

function getSegment(cust) {
  if (!cust.totalSpent) return 'new';
  if (cust.totalSpent > 10000) return 'high';
  if ((cust.visits || 0) > 5) return 'loyal';
  if ((cust.monthsSince || 0) >= 6) return 'risk';
  return 'all';
}

export default function BusinessCustomerCampaigns() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState('all');
  const [minSpent, setMinSpent] = useState('');
  const [minOrders, setMinOrders] = useState('');
  const [inactiveMonths, setInactiveMonths] = useState('');
  const [lastPurchaseFrom, setLastPurchaseFrom] = useState('');
  const [lastPurchaseTo, setLastPurchaseTo] = useState('');
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // selected customer ids
  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState({ segment: 'all', minSpent: '', minOrders: '', inactiveMonths: '', city: 'all', search: '' });
  const [hasSearched, setHasSearched] = useState(false);

  // Extract unique cities and categories for filter dropdowns
  const uniqueCities = Array.from(new Set(customers.flatMap(c => c.city ? [c.city] : [])));
  const uniqueCategories = Array.from(new Set(customers.flatMap(c => c.categories || [])));

  // Add a function to fetch filtered customers
  const fetchFilteredCustomers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.segment && filters.segment !== 'all') params.segment = filters.segment;
      if (filters.minSpent) params.minSpent = filters.minSpent;
      if (filters.minOrders) params.minOrders = filters.minOrders;
      if (filters.inactiveMonths) params.inactiveMonths = filters.inactiveMonths;
      if (filters.city && filters.city !== 'all') params.city = filters.city;
      if (filters.search) params.search = filters.search;
      const res = await getFilteredCustomers(params);
      setCustomers(res.data || []);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  // Filter logic
  let filtered = customers.filter(cust => {
    if (segment !== 'all' && getSegment(cust) !== segment) return false;
    if (minSpent && (cust.totalSpent || 0) < Number(minSpent)) return false;
    if (minOrders && (cust.visits || 0) < Number(minOrders)) return false;
    if (inactiveMonths && (cust.monthsSince || 0) < Number(inactiveMonths)) return false;
    if (city && city !== 'all' && cust.city !== city) return false;
    if (search && !(
      (cust.name && cust.name.toLowerCase().includes(search.toLowerCase())) ||
      (cust.phoneNumber && cust.phoneNumber.includes(search))
    )) return false;
    return true;
  });

  // Message/image preview
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSend = async () => {
    let imageData = image;
    if (image && image instanceof File) {
      // Convert image file to base64
      imageData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]); // get base64 part only
        reader.onerror = reject;
        reader.readAsDataURL(image);
      });
    }
    // Map selected to real phone numbers
    const selectedPhoneNumbers = customers
      .filter(cust => selected.includes(cust.id || cust.phoneNumber))
      .map(cust => cust.phoneNumber);
    try {
      const res = await sendCustomerCampaign({
        customers: selectedPhoneNumbers,
        message,
        image: imageData || '',
      });
      // Show summary of results
      const results = res.data.results || [];
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      setSnackbar({
        open: true,
        message: `Sent to ${successCount} customers${failCount > 0 ? `, ${failCount} failed` : ''}`,
        severity: failCount > 0 ? 'warning' : 'success',
      });
      setSelected([]);
      setMessage('');
      setImage(null);
    } catch (err) {
      setSnackbar({ open: true, message: getApiError(err), severity: 'error' });
    }
  };

  const handleReset = () => {
    setSegment('all');
    setMinSpent('');
    setMinOrders('');
    setInactiveMonths('');
    setCity('all');
    setSearch('');
  };

  // Select all logic
  const allSelected = filtered.length > 0 && filtered.every(cust => selected.includes(cust.id || cust.phoneNumber));
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(filtered.map(cust => cust.id || cust.phoneNumber));
    else setSelected([]);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'top', justifyContent: 'center', py: { xs: 2, md: 6 }, px: { xs: 1, md: 0 } }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, width: '100%', borderRadius: 6, boxShadow: 0, border: '1px solid', borderColor: 'divider', mx: 'auto', background: 'background.paper' }}>
        <Typography variant="h4" fontWeight={700} color="text.primary" mb={1}>
          Customer Campaigns
        </Typography>
        <Divider sx={{ mb: 3 }} />
        {/* Filter Panel */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 6, bgcolor: 'background.paper', boxShadow: 1, border: '1px solid #e3e8ee' }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 2.5 : 2}
            flexWrap="wrap"
            alignItems={{ md: 'center' }}
            rowGap={isMobile ? 2.5 : 2}
            columnGap={isMobile ? 0 : 2}
            sx={{
              '& > *': { mb: isMobile ? 1.5 : 0, minWidth: { xs: 180, sm: 120 } },
              pb: 1,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: '0.875rem' }}>Segment</InputLabel>
              <Select 
                value={filters.segment} 
                label="Segment" 
                onChange={e => setFilters(f => ({ ...f, segment: e.target.value }))}
                sx={{
                  '& .MuiSelect-select': {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    fontSize: '0.875rem'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    },
                    '&:hover fieldset': {
                      borderColor: '#bdbdbd'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: 'primary.main'
                    },
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'white',
                      padding: '0 4px'
                    }
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem'
                      }
                    }
                  }
                }}
              >
                {SEGMENTS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Min Total Spent (₹)"
              type="number"
              size="small"
              value={filters.minSpent}
              onChange={e => setFilters(f => ({ ...f, minSpent: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              sx={{ minWidth: 120 }}
            />
            <TextField
              label="Min Orders"
              type="number"
              size="small"
              value={filters.minOrders}
              onChange={e => setFilters(f => ({ ...f, minOrders: e.target.value }))}
              sx={{ minWidth: 100 }}
            />
            <TextField
              label="Inactive for (months)"
              type="number"
              size="small"
              value={filters.inactiveMonths}
              onChange={e => setFilters(f => ({ ...f, inactiveMonths: e.target.value }))}
              InputProps={{ startAdornment: <FilterAltIcon color="primary" /> }}
              sx={{ minWidth: 120 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: '0.875rem' }}>City</InputLabel>
              <Select 
                value={filters.city} 
                label="City" 
                onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                sx={{
                  '& .MuiSelect-select': {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    fontSize: '0.875rem'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#e0e0e0'
                    },
                    '&:hover fieldset': {
                      borderColor: '#bdbdbd'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-focused': {
                      color: 'primary.main'
                    },
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                      backgroundColor: 'white',
                      padding: '0 4px'
                    }
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem'
                      }
                    }
                  }
                }}
                              >
                  <MenuItem value="all">All</MenuItem>
                  {uniqueCities.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
            </FormControl>
            <TextField
              label="Search by name or phone"
              size="small"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              InputProps={{ startAdornment: <SearchIcon color="primary" /> }}
              sx={{ minWidth: 160, flex: 1 }}
            />
            <Button variant="contained" color="primary" onClick={fetchFilteredCustomers} sx={{ minWidth: 120, height: 40 }}>Search</Button>
            <Button variant="outlined" color="secondary" onClick={() => { setFilters({ segment: 'all', minSpent: '', minOrders: '', inactiveMonths: '', city: 'all', search: '' }); setCustomers([]); setHasSearched(false); }} sx={{ minWidth: 120, height: 40 }}>Reset Filters</Button>
          </Stack>
        </Paper>
        {/* Results Table */}
        <TableContainer sx={{ mb: 3, borderRadius: 3, boxShadow: 0, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 900, fontSize: isMobile ? 13 : 15 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Checkbox checked={allSelected} onChange={handleSelectAll} />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Total Spent</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Last Purchase</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Inactive (months)</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Orders</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>City</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasSearched ? (
                <TableRow><TableCell colSpan={8} align="center">app/Apply filter to see data</TableCell></TableRow>
              ) : loading ? (
                <TableRow><TableCell colSpan={8} align="center"><span style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8}}><span>Loading...</span><span>{React.createElement(require('@mui/material').CircularProgress, {size:22})}</span></span></TableCell></TableRow>
              ) : customers.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">No customers found for these filters.</TableCell></TableRow>
              ) : customers.map((cust, idx) => (
                <TableRow key={cust.id || cust.phoneNumber} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <Checkbox
                      checked={selected.includes(cust.id || cust.phoneNumber)}
                      onChange={e => {
                        const id = cust.id || cust.phoneNumber;
                        if (e.target.checked) setSelected([...selected, id]);
                        else setSelected(selected.filter(i => i !== id));
                      }}
                    />
                  </TableCell>
                  <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar src={cust.avatar} alt={cust.name || cust.phoneNumber} sx={{ width: 32, height: 32, bgcolor: 'action.selected', color: 'text.primary', fontWeight: 700 }}>
                        {(cust.name || cust.phoneNumber || '')[0]}
                      </Avatar>
                      <Typography fontWeight={600}>{cust.name || cust.phoneNumber}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{cust.phoneNumber}</TableCell>
                  <TableCell>₹{(cust.totalSpent || 0).toLocaleString()}</TableCell>
                  <TableCell>{cust.lastPurchase ? new Date(cust.lastPurchase).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{cust.monthsSince !== undefined ? cust.monthsSince : '-'}</TableCell>
                  <TableCell>{cust.visits || '-'}</TableCell>
                  <TableCell>{cust.city || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Message & Image Section */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 6, bgcolor: 'background.paper', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={700} mb={2} color="text.primary">
            Whatsapp/App Message
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <TextField
              label="Message"
              multiline
              minRows={2}
              maxRows={5}
              value={message}
              onChange={e => setMessage(e.target.value)}
              fullWidth
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              component="label"
              startIcon={<ImageIcon />}
              sx={{ minWidth: 140 }}
            >
              Upload Image
              <input type="file" accept="image/*" hidden onChange={handleImageChange} />
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              disabled={selected.length === 0 || !message}
              onClick={handleSend}
              sx={{ minWidth: 160, position: { xs: 'sticky', sm: 'static' }, bottom: { xs: 0, sm: 'auto' } }}
            >
              Send to Selected
            </Button>
          </Stack>
          {/* Preview */}
          {(message || image) && (
            <Box mt={2} display="flex" alignItems="center" gap={2}>
              {image && (
                <img
                  src={URL.createObjectURL(image)}
                  alt="Preview"
                  style={{ maxHeight: 60, borderRadius: 8, border: '1px solid #E5E7EB' }}
                />
              )}
              {message && (
                <Paper sx={{ p: 1.5, bgcolor: 'action.selected', borderRadius: 2, fontSize: 16, fontWeight: 500, color: 'text.primary', boxShadow: 0 }}>
                  {message}
                </Paper>
              )}
            </Box>
          )}
        </Paper>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2500}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} variant="filled" sx={{ fontWeight: 600 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
} 
